import {
    CameraPhotoVehicles,
    CancellationReasons,
    ContraventionStatus,
    PCNDuplicate,
    PermitData,
    PhotoType,
    PhotoVersion,
} from '@EcamModel/model';
import { PolygonBox } from '@EcamModel/model/AiPlateDetectionResponse';
import EditIcon from '@assets/jsx-icon/EditIcon';
import MinimizeImageIcon from '@assets/jsx-icon/MinimizeImageIcon';
import RestartImageIcon from '@assets/jsx-icon/RestartImageIcon';
import ZoomImageIcon from '@assets/jsx-icon/ZoomImageIcon';
import usePopUp, { UsePopUpReturnType } from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Popover,
    Slider,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import color from '@theme/Colors';
import Konva from 'konva';
import moment from 'moment';
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { Circle, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from 'react-konva';
import { PCInfo } from '..';
import { DuplicatePcnCard } from './DuplicatePCDetail';
import EvidencePhotoItem from './EvidencePhotoItem';
import LivePhotoPreviewDialog from './LivePhotoPreviewDialog';
import LivePhotosTimeline, { LivePhotosTimelineHandle } from './LivePhotosTimeline';
import PopUpEditImage from './PopUpEditImage';

import { ICancelGroupPotentialContraventionDetail } from '@EcamModel/controllers/IGroupPotentialContraventionHttpController';
import { VerificationAudit } from '@EcamModel/model/VerificationAudit';
import BoundingBoxVehicle from '@assets/jsx-icon/BoundingBoxVehicle';
import CoordinatesIcon from '@assets/jsx-icon/CoordinatesIcon';
import CropPlate from '@assets/jsx-icon/CropPlate';
import PopUpWarning from '@components/PopUpWarning';
import { DrawerContext } from '@components/drawer/useDrawer';
import { appConfig } from '@configs/index';
import { getCenterFrame } from '@pages/cameras/details/utils';
import { photoTypeColorMap } from '@pages/manual-create-pcn/configs';
import { motion } from 'framer-motion';
import { formatUKDate, formatUKTime } from 'src/helpers';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper, useControls } from 'react-zoom-pan-pinch';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { PotentialContraventionContext } from '../hooks/PotentialContraventionProvider';
import { CropArea } from '../hooks/useEditImage';
import { useImagePreviewDimensions } from '../hooks/useImageDimensions';
import ConfirmApproveDialog, { VerificationSummary } from './ConfirmApproveDialog';
import { OrangeStack } from './PCDetail';
import PopUpPlateEditing, { convertToCropArea } from './PopUpPlateEditing';
import PopUpReplacePhoto from './PopUpReplacePhoto';
import useHorizontalScroll from './useHorizontalScroll';

export type PhotoItem = {
    title: string;
    blob?: string;
    timeZone: string;
    date: Date | undefined;
    plate: string;
    photo: CameraPhotoVehicles | undefined;
    rootPhoto?: CameraPhotoVehicles;
    photoType: PhotoType;
    photoVersion: PhotoVersion;
    photoVehicleId: number;
    rootBlob: string;
    cameraId: number;
};

type Props = {
    readonly?: boolean;
    pc: PCInfo;
    selected?: boolean;
    onSelect?(pc: PCInfo): any;
    cancellationReasons: CancellationReasons[];
    handleCancelPc?: ((props: ICancelGroupPotentialContraventionDetail) => void) | undefined;
    handleSelectEvidencePhotoItem: (evidencePhoto: PhotoItem) => void;
    selectedEvidencePhotoItem: PhotoItem;
    VerificationAudit?: VerificationAudit;
    prevGeoZones: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    popUpOpenLivePhotos: UsePopUpReturnType;
    onOpenCancelPopup?: (preSelectedReasonIndex?: number) => void;
    onSkip?: () => void;
    skipDisabled?: boolean;
    onApprove?: () => void;
    approveDisabled?: boolean;
    gracePeriodSeconds?: number | null;
    hasDuplicates?: boolean;
    pcnDuplicatesWarning?: PCNDuplicate[];
    filteredDuplicates?: PCNDuplicate[];
    currentStep?: number;
    onOpenDuplicatesDetail?: () => void;
    handleOpenPopUpGrPcDuplicate?: () => void;
    permitData?: PermitData;
    onOpenPermitDetail?: () => void;
    onOpenCancelPermit?: () => void;
    isNoPcRestriction?: boolean;
    isExemptionPlate?: boolean;
    isEVWarning?: boolean;
    isNoOverstay?: boolean;
    plateInput?: string;
    onPlateInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onPlateKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onPlateBlur?: () => void;
    onSavePlate?: () => void;
    updatingPlate?: boolean;
    isReadOnlyCondition?: boolean;
    isPCApprove?: boolean
};

interface TransformWrapperComponentProps {
    handleTransform?: (e: any) => void;
    loading?: boolean;
    displayWidth?: number;
    displayHeight?: number;
    scale?: number;
    scaleZoomChangeCursor?: number;
    image?: any;
    stageRef?: React.RefObject<any>;
    customTransformComponent?: React.ReactNode;
    cropArea?: CropArea | null;
    cameraPhotoVehicles?: CameraPhotoVehicles[];
    useCustomDisplayWidth?: boolean;
    prevGeoZones?: {
        x: number;
        y: number;
        geoZoneId: number | undefined;
    }[][];
    vehicleBoundingBoxes?: {
        x: number;
        y: number;
        width: number;
        height: number;
        plate?: string;
        color?: string;
    }[];
    adjustmentEnabled?: boolean;
    initialBrightness?: number;
    initialContrast?: number;
    showZoomIn?: boolean;
    showZoomOut?: boolean;
    onSaveAdjustment?: (dataUrl: string) => void;
    transformWrapperRef?: React.Ref<ReactZoomPanPinchRef>;
    showOverlays?: boolean;
    onInteraction?: () => void;
}

export const formatDuration = (from, to) => {
    const fromTime = moment(from);
    const toTime = moment(to);
    const duration = moment.duration(toTime.diff(fromTime));

    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    const ms = duration.milliseconds();

    const pad = (num: number) => String(num).padStart(2, '0');
    const padMs = (num: number) => String(num).padStart(3, '0');
    return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s ${padMs(ms)}ms`;
};

export const keyToPhotoType: Record<string, PhotoType> = {
    '1': PhotoType.Entry,
    '2': PhotoType.Validation,
    '3': PhotoType.Exit,
    '4': PhotoType.UnobstructedPlate,
};

export const convertWarpedBoxToCropArea = (warpedBox: number[]): CropArea | null => {
    if (!warpedBox || warpedBox.length !== 8) {
        return null;
    }

    const [x1, y1, x2, y2, x3, y3, x4, y4] = warpedBox;
    const x = Math.min(x1, x2, x3, x4);
    const y = Math.min(y1, y2, y3, y4);
    const width = Math.max(x1, x2, x3, x4) - x;
    const height = Math.max(y1, y2, y3, y4) - y;

    return { x, y, width, height };
};

/**
 * Resolves the vehicle bounding box (car.warpedBox) for an evidence photo.
 *
 * Matching order (stops at first match):
 *  1. AiPlateCandidate.car.warpedBox  — populated on the V2 review path
 *  2. Bbox match: find the plate in CameraPhoto.AiPlateDetectionResponse.plates[]
 *     whose warpedBox matches this vehicle's X1..Y4 within ±1px tolerance
 *  3. RawPlate text match: normalized comparison against plate.text
 *  Returns undefined if nothing matches — caller should not render a vehicle box.
 */
export function findVehicleWarpedBox(vehicle: CameraPhotoVehicles): PolygonBox | undefined {
    // 1. AiPlateCandidate present (V2 review path / future-proofing)
    const candidateBox = vehicle.AiPlateCandidate?.car?.warpedBox;
    if (candidateBox && candidateBox.length === 8) return candidateBox;

    const plates = vehicle.CameraPhoto?.AiPlateDetectionResponse?.plates;
    if (!plates?.length) return undefined;

    // 2. Bbox match against stored X1..Y4 with ±1px tolerance
    const TOLERANCE = 1.0;
    const { X1, Y1, X2, Y2, X3, Y3, X4, Y4 } = vehicle;
    const bboxMatch = plates.find((p) => {
        const wb = p.warpedBox;
        if (!wb || wb.length !== 8) return false;
        return (
            Math.abs(wb[0] - X1) <= TOLERANCE &&
            Math.abs(wb[1] - Y1) <= TOLERANCE &&
            Math.abs(wb[2] - X2) <= TOLERANCE &&
            Math.abs(wb[3] - Y2) <= TOLERANCE &&
            Math.abs(wb[4] - X3) <= TOLERANCE &&
            Math.abs(wb[5] - Y3) <= TOLERANCE &&
            Math.abs(wb[6] - X4) <= TOLERANCE &&
            Math.abs(wb[7] - Y4) <= TOLERANCE
        );
    });
    if (bboxMatch?.car?.warpedBox && bboxMatch.car.warpedBox.length === 8) return bboxMatch.car.warpedBox;

    // 3. Normalized RawPlate text match
    const normalize = (s?: string | null) => (s ?? '').toUpperCase().replace(/[\s-]/g, '');
    const rawNorm = normalize(vehicle.RawPlate);
    if (rawNorm) {
        const textMatch = plates.find((p) => normalize(p.text) === rawNorm);
        if (textMatch?.car?.warpedBox && textMatch.car.warpedBox.length === 8) return textMatch.car.warpedBox;
    }

    return undefined;
}

export const pcImage = (pc: PCInfo) => {
    const entryPhoto = pc.EntryPhotos?.length
        ? pc.EntryPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;
    const validationPhoto = pc.ValidationPhotos?.length
        ? pc.ValidationPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;
    const exitPhoto = pc.ExitPhotos?.length
        ? pc.ExitPhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;
    const unobstructedPlatePhoto = pc.UnobstructedPlatePhotos?.length
        ? pc.UnobstructedPlatePhotos.reduce((max, item) => ((item.Id ?? 0) > (max.Id ?? 0) ? item : max))
        : undefined;

    const entryTime = entryPhoto?.CameraPhoto?.CaptureAt;
    const validationTime = validationPhoto?.CameraPhoto?.CaptureAt;
    const exitTime = exitPhoto?.CameraPhoto?.CaptureAt;
    const overviewTime = unobstructedPlatePhoto?.CameraPhoto?.CaptureAt;

    const rootEntryPhoto = pc.EntryPhotos?.[0];
    const rootValidationPhoto = pc.ValidationPhotos?.[0];
    const rootExitPhoto = pc.ExitPhotos?.[0];
    const rootUnobstructedPlatePhotos = pc.UnobstructedPlatePhotos?.[0];

    const cameraId = pc.CameraId;
    return {
        entryPhoto,
        validationPhoto,
        exitPhoto,
        unobstructedPlatePhoto,
        entryTime,
        validationTime,
        exitTime,
        overviewTime,
        rootEntryPhoto,
        rootValidationPhoto,
        rootExitPhoto,
        rootUnobstructedPlatePhotos,
        cameraId,
    };
};

const EvidencePhotoGroup = (props: Props) => {
    const { pc, cancellationReasons, popUpOpenLivePhotos, handleCancelPc } = props;
    const { Timezone, ExitPhotos, ValidationPhotos } = pc;
    const { groupPcId } = useParams();
    const imageDimensions = useImagePreviewDimensions({});
    const { open: openDrawer } = useContext(DrawerContext);
    const backdrop = useBackdrop();
    const queryClient = useQueryClient();

    const pcContext = useContext(PotentialContraventionContext);

    const { selectedPc, setSelectedPc, setGroupPc, contraventionId } = pcContext;

    const {
        entryPhoto,
        exitPhoto,
        validationPhoto,
        unobstructedPlatePhoto,
        entryTime,
        exitTime,
        validationTime,
        overviewTime,
        rootEntryPhoto,
        rootExitPhoto,
        rootValidationPhoto,
        rootUnobstructedPlatePhotos,
        cameraId: camId,
    } = pcImage(pc);

    const isMoreThan10Minutes = (from: Date | undefined, to: Date | undefined) => {
        const fromTime = moment(from);
        const toTime = moment(to);
        const duration = moment.duration(toTime.diff(fromTime));

        return duration.asMinutes() > 10;
    };

    const cameraExitPhotosId = ExitPhotos?.[0]?.CameraPhotoId;
    const cameraValidationPhotosId = ValidationPhotos?.[0]?.CameraPhotoId;

    const isCheckedDuplicateImageExitAndValidationPhoto = cameraExitPhotosId === cameraValidationPhotosId;
    const isCheckDurationExceeding5Minutes = isMoreThan10Minutes(entryTime, exitTime);

    const cancellationNotes = [
        {
            id: 1,
            label: 'External graphic',
        },
        {
            id: 2,
            label: 'Suspicion that the vehicle has passed through the camera many times',
        },
        {
            id: 3,
            label: 'Suspicion the car is not parked',
        },
    ];

    const [warningCheckedPcHasProperty, setWarningCheckedPcHasProperty] = useState(() => {
        const initialCheckedValues = {};
        cancellationNotes.forEach((checkbox) => {
            initialCheckedValues[checkbox.label] = false;
        });
        return initialCheckedValues;
    });

    const getCheckedLabels = () => {
        return cancellationNotes
            .filter((checkbox) => warningCheckedPcHasProperty[checkbox.label])
            .map((checkbox) => checkbox.label);
    };

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setWarningCheckedPcHasProperty((prevState) => ({
            ...prevState,
            [name]: checked,
        }));
    };

    const durationTime = formatDuration(pc.EntryAt, pc.ExitAt);

    const timingInfo = React.useMemo(() => {
        const pcEntryTime = pc.EntryAt;
        const pcExitTime = pc.ExitAt;
        if (!pcEntryTime || !pcExitTime) return null;
        const contraventionDate = formatUKDate(pcEntryTime, pc.Timezone);
        const entered = formatUKTime(pcEntryTime, pc.Timezone);
        const exited = formatUKTime(pcExitTime, pc.Timezone);
        const duration = formatDuration(pcEntryTime, pcExitTime);

        const gracePeriodSeconds = props.gracePeriodSeconds ?? null;
        let overstayInfo: { isOverstay: boolean; formatted: string } | null = null;
        if (gracePeriodSeconds != null) {
            const durationMs = moment(pcExitTime).diff(moment(pcEntryTime));
            const gracePeriodMs = gracePeriodSeconds * 1000;
            const overstayMs = durationMs - gracePeriodMs;
            const isOverstay = overstayMs > 0;
            const absMs = Math.abs(overstayMs);
            const h = Math.floor(absMs / 3600000);
            const m = Math.floor((absMs % 3600000) / 60000);
            const s = Math.floor((absMs % 60000) / 1000);
            const ms = absMs % 1000;
            const pad = (n: number) => String(n).padStart(2, '0');
            const padMs = (n: number) => String(n).padStart(3, '0');
            const formatted = `${pad(h)}h ${pad(m)}m ${pad(s)}s ${padMs(ms)}ms`;
            overstayInfo = { isOverstay, formatted };
        }

        return { contraventionDate, entered, exited, duration, overstayInfo };
    }, [pc.EntryAt, pc.ExitAt, pc.Timezone, props.gracePeriodSeconds]);

    const isNoOverstayAlert = !!(timingInfo?.overstayInfo != null && !timingInfo.overstayInfo.isOverstay);

    const verificationSummary = React.useMemo<VerificationSummary>(() => {
        const gracePeriodSec = props.gracePeriodSeconds ?? 0;
        const contraventionLabel = pc.ContraventionType || pc.ContraventionReason?.Code || '—';
        return {
            zone: pc.Zone?.PublicName ?? '—',
            contravention: contraventionLabel,
            date: timingInfo?.contraventionDate ?? '—',
            plate: pc.Plate ?? '',
            vehicle: {
                make: pc.VehicleMake,
                model: pc.VehicleModel,
                color: pc.VehicleColor,
                fuelType: pc.VehicleFuelType,
            },
            permit: { hasPermit: !!(props.permitData?.hasPermit ?? pc.PermitData?.hasPermit) },
            duplicates: { hasDuplicate: !!props.hasDuplicates },
            geoZone: { duration: durationTime, gracePeriodSec },
            overstay: {
                isOverstay: !!timingInfo?.overstayInfo?.isOverstay,
                duration: timingInfo?.overstayInfo?.formatted ?? '00h 00m 00s',
            },
            entered: timingInfo?.entered ?? '—',
            exited: timingInfo?.exited ?? '—',
            isForeign: !!pc.IsForeign,
        };
    }, [
        pc.ZoneName,
        pc.ContraventionReason,
        pc.ContraventionType,
        pc.Plate,
        pc.VehicleMake,
        pc.VehicleModel,
        pc.VehicleColor,
        pc.VehicleFuelType,
        pc.PermitData,
        pc.IsForeign,
        props.permitData,
        props.hasDuplicates,
        props.gracePeriodSeconds,
        durationTime,
        timingInfo,
    ]);

    const popUpEditImage = usePopUp();
    const popUpPlateEditing = usePopUp();
    const popUpReplacePhoto = usePopUp();
    const popUpConfirmCancelPcHasProperty = usePopUp();

    const handleCancelPCHasProperty = () => {
        const checkedLabelsString = getCheckedLabels().join(', ');
        const idReason = cancellationReasons.find((c) => c.Id === 5);

        const _props: ICancelGroupPotentialContraventionDetail = {
            CancellationNotes: checkedLabelsString,
            CancellationReasonId: idReason?.Id!,
            Id: pc.GroupPotentialContraventionId!,
        };
        handleCancelPc?.(_props);
        popUpConfirmCancelPcHasProperty.onClose?.();
    };

    const [fallbackPreviewIndex, setFallbackPreviewIndex] = useState(-1);

    // Slider state
    const { scrollRef: sliderRef, scrollState, updateScrollState } = useHorizontalScroll();
    const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
    const timelineRef = useRef<LivePhotosTimelineHandle>(null);

    const scrollToIndex = (index: number) => {
        const el = sliderRef.current;
        if (!el) return;
        const child = el.children[index] as HTMLElement;
        if (!child) return;
        setCurrentSliderIndex(index);
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const containerCenter = el.offsetWidth / 2;
        el.scrollTo({ left: childCenter - containerCenter, behavior: 'smooth' });
    };

    const handleSliderScroll = (dir: 'left' | 'right') => {
        const el = sliderRef.current;
        if (!el) return;
        const maxIndex = el.children.length - 1;
        const nextIndex =
            dir === 'left' ? Math.max(0, currentSliderIndex - 1) : Math.min(maxIndex, currentSliderIndex + 1);
        scrollToIndex(nextIndex);
    };

    const scaleSize = 1920 / imageDimensions.width;

    const buildPhoto = ({ title, photo, rootPhoto, date, photoType, cameraId }: Partial<PhotoItem>) => ({
        title,
        date,
        photo,
        photoType,
        cameraId: camId,
        plate: pc.Plate,
        timeZone: Timezone,
        photoVehicleId: photo?.Id,
        plateRootImage: photo?.BlobName,
        blob: photo?.CameraPhoto?.BlobName,
        photoVersion: photo?.CameraPhoto?.Version,
        rootBlob: rootPhoto?.CameraPhoto?.BlobName,
    });

    const fullPlateSlot = buildPhoto({
        title: 'Full license plate photo',
        photo: unobstructedPlatePhoto,
        rootPhoto: rootUnobstructedPlatePhotos,
        date: overviewTime,
        photoType: PhotoType.UnobstructedPlate,
    });

    const photos: ReturnType<typeof buildPhoto>[] =
        validationPhoto?.Id === entryPhoto?.Id
            ? [
                  buildPhoto({
                      title: 'Contravention validation photo',
                      photo: validationPhoto,
                      rootPhoto: rootValidationPhoto,
                      date: validationTime,
                      photoType: PhotoType.Validation,
                  }),
                  fullPlateSlot,
              ]
            : [
                  buildPhoto({
                      title: 'Entry photo',
                      photo: entryPhoto,
                      rootPhoto: rootEntryPhoto,
                      date: entryTime,
                      photoType: PhotoType.Entry,
                  }),
                  buildPhoto({
                      title: 'Contravention validation photo',
                      photo: validationPhoto,
                      rootPhoto: rootValidationPhoto,
                      date: validationTime,
                      photoType: PhotoType.Validation,
                  }),
                  buildPhoto({
                      title: 'Exit photo',
                      photo: exitPhoto,
                      rootPhoto: rootExitPhoto,
                      date: exitTime,
                      photoType: PhotoType.Exit,
                  }),
                  fullPlateSlot,
              ];

    useEffect(() => {
        updateScrollState();
    }, [photos.length]);

    const photosRef = useRef(photos);
    photosRef.current = photos;

    const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);

    const callbacksRef = useRef({
        onSkip: props.onSkip,
        skipDisabled: props.skipDisabled,
        onOpenCancelPopup: props.onOpenCancelPopup,
        onApprove: props.onApprove,
        approveDisabled: props.approveDisabled,
    });
    callbacksRef.current = {
        onSkip: props.onSkip,
        skipDisabled: props.skipDisabled,
        onOpenCancelPopup: props.onOpenCancelPopup,
        onApprove: props.onApprove,
        approveDisabled: props.approveDisabled,
    };

    useEffect(() => {
        if (!props.selected) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                return;
            }
            if (document.querySelector('.MuiDialog-root, .MuiModal-root')) return;
            if (e.key === 'ArrowLeft') {
                if (fallbackPreviewIndex >= 0) return;
                handleSliderScroll('left');
            } else if (e.key === 'ArrowRight') {
                if (fallbackPreviewIndex >= 0) return;
                handleSliderScroll('right');
            } else if (['1', '2', '3', '4'].includes(e.key)) {
                const targetType = keyToPhotoType[e.key];
                const currentPhotos = photosRef.current;
                const targetPhoto = currentPhotos.find((p) => p.photoType === targetType);
                if (targetPhoto) {
                    const vehicleId = targetPhoto.photo?.Id;
                    const opened =
                        vehicleId && timelineRef.current ? timelineRef.current.openPreviewAtPhotoId(vehicleId) : false;
                    if (!opened) {
                        const idx = currentPhotos.indexOf(targetPhoto);
                        setFallbackPreviewIndex(idx >= 0 ? idx : 0);
                    }
                }
            } else if (e.key === 'Escape') {
                if (fallbackPreviewIndex >= 0 || confirmApproveOpen) return;
                if (!props.isReadOnlyCondition) return;
                callbacksRef.current.onOpenCancelPopup?.();
            } else if (e.key.toLowerCase() === 's') {
                if (fallbackPreviewIndex >= 0 || confirmApproveOpen) return;
                if (!props.isReadOnlyCondition) return;
                if (callbacksRef.current.skipDisabled) return;
                callbacksRef.current.onSkip?.();
            } else if (e.key === 'Enter') {
                if (fallbackPreviewIndex >= 0 || confirmApproveOpen) return;
                if (!props.isReadOnlyCondition) return;
                if (!callbacksRef.current.approveDisabled) {
                    setConfirmApproveOpen(true);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSliderIndex, props.selected, fallbackPreviewIndex, confirmApproveOpen, props.isReadOnlyCondition]);

    const fallbackItems = photos
        .map((p) => p.photo)
        .filter((v): v is CameraPhotoVehicles => v != null && v.CameraPhoto != null)
        .map((v) => ({ ...v.CameraPhoto!, CameraPhotoVehicles: [v] }));

    const groupRef = useRef<HTMLDivElement>(null);
    const isInitialMount = useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (props.selected && groupRef.current) {
            groupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [props.selected]);

    useEffect(() => {
        const el = sliderRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            handleSliderScroll(e.deltaY > 0 ? 'right' : 'left');
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [handleSliderScroll]);

    const handleSetSelectedItem = (item: CameraPhotoVehicles) => {
        if (!unobstructedPlatePhoto) return;
        const photoItem: PhotoItem = buildPhoto({
            title: 'Full license plate',
            photo: item,
            rootPhoto: rootUnobstructedPlatePhotos,
            date: overviewTime,
            photoType: PhotoType.UnobstructedPlate,
        }) as PhotoItem;

        props.handleSelectEvidencePhotoItem(photoItem);
    };

    return (
        <Stack
            ref={groupRef}
            direction={'column'}
            width={'100%'}
            sx={{
                border: `1px solid ${color.grey300}`,
                borderRadius: 1,
                transition: 'all  0.3s',
                cursor: 'default',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
            }}
        >
            <Stack
                alignItems={'start'}
                sx={{
                    background: color.grey100,
                    borderRadius: '4px 4px 0px 0px',
                    paddingY: '12px',
                    paddingX: '24px',
                    position: 'relative',
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    gap={0.5}
                    sx={{ flexWrap: 'nowrap' }}
                >
                    {(() => {
                        const allItems = [
                            {
                                label: 'Contravention date',
                                value: timingInfo?.contraventionDate ?? '—',
                            },
                            {
                                label: 'Entry',
                                value: timingInfo?.entered ?? '—',
                            },
                            {
                                label: 'Exited',
                                value: timingInfo?.exited ?? '—',
                            },
                            {
                                label: 'Duration',
                                value: timingInfo?.duration ?? '—',
                            },
                            {
                                label: 'Grace period',
                                value: props.gracePeriodSeconds != null ? `${props.gracePeriodSeconds}s` : '—',
                                isGracePeriod: true,
                            },
                            ...(timingInfo?.overstayInfo
                                ? [
                                      {
                                          label: 'Overstay',
                                          value: timingInfo.overstayInfo.isOverstay
                                              ? timingInfo.overstayInfo.formatted
                                              : 'No Overstay',
                                          isOverstay: timingInfo.overstayInfo.isOverstay,
                                          isOverstayChip: timingInfo.overstayInfo.isOverstay,
                                          isNoOverstayChip: !timingInfo.overstayInfo.isOverstay,
                                      },
                                  ]
                                : []),
                            {
                                label: 'Contravention type',
                                value: selectedPc?.ContraventionType || '—',
                            },
                            {
                                label: 'Permit info',
                                value: selectedPc?.PermitData?.permitInfo?.isGlobalPermit
                                    ? 'Has global permit'
                                    : selectedPc?.PermitData?.hasPermit
                                      ? 'Has permit'
                                      : 'No permit',
                                isHasPermit:
                                    selectedPc?.PermitData?.permitInfo?.isGlobalPermit ||
                                    selectedPc?.PermitData?.hasPermit,
                                isPermitClickable:
                                    selectedPc?.PermitData?.permitInfo?.isGlobalPermit ||
                                    selectedPc?.PermitData?.hasPermit,
                            },
                            {
                                label: 'Duplicates',
                                value: props.hasDuplicates ? 'Has duplicates' : 'No duplicates',
                                isHasDuplicates: props.hasDuplicates,
                                isDuplicatesClickable: props.hasDuplicates,
                            },
                            {
                                label: 'Multiple Offenders',
                                value: props.isNoPcRestriction ? 'Yes' : 'No',
                                condition: !!props.isNoPcRestriction,
                            },
                            {
                                label: 'Exemption Plate',
                                value: props.isExemptionPlate ? 'Yes' : 'No',
                                condition: !!props.isExemptionPlate,
                            },
                            {
                                label: 'EV Warning',
                                value: props.isEVWarning ? 'Yes' : 'No',
                                condition: !!props.isEVWarning,
                            },
                        ].filter((item) => (item as any).condition !== false);

                        const renderItem = (item: any, index: number) => (
                            <Stack
                                key={item.label}
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                                sx={{ flex: '1 1 auto' }}
                            >
                                {index > 0 && (
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ borderColor: color.grey300, mx: 1 }}
                                    />
                                )}
                                <Stack direction="column" sx={{ minWidth: 0 }}>
                                    <Typography fontSize={13} color="text.secondary" noWrap>
                                        {item.label}:
                                    </Typography>
                                    {item.isDuplicatesClickable ? (
                                        <DuplicatesChip
                                            pcnDuplicatesWarning={props.pcnDuplicatesWarning ?? []}
                                            filteredDuplicates={props.filteredDuplicates ?? []}
                                            currentStep={props.currentStep}
                                            onOpenDetail={props.onOpenDuplicatesDetail}
                                            handleOpenPopUpGrPcDuplicate={props?.handleOpenPopUpGrPcDuplicate}
                                        />
                                    ) : item.isPermitClickable ? (
                                        <PermitChip
                                            permitData={props.permitData!}
                                            onOpenDetail={props.onOpenPermitDetail}
                                            onCancelPermit={props.onOpenCancelPermit}
                                        />
                                    ) : item.isOverstayChip ? (
                                        <PulsingChip
                                            label={item.value}
                                            mainColor={color.success}
                                            textColor={color.success}
                                            animationKey="pingOverstay"
                                        />
                                    ) : item.isNoOverstayChip ? (
                                        <PulsingChip
                                            label="No Overstay"
                                            mainColor={color.danger}
                                            textColor={color.darkDanger}
                                            animationKey="pingNoOverstay"
                                        />
                                    ) : (
                                        <motion.div
                                            initial={{ scale: 1 }}
                                            animate={
                                                item.isOverstay || item.condition
                                                    ? { scale: [1, 1.1, 1] }
                                                    : { scale: 1 }
                                            }
                                            transition={{
                                                duration: 1.2,
                                                ease: 'easeInOut',
                                            }}
                                        >
                                            <Typography
                                                fontSize={15}
                                                fontWeight={550}
                                                sx={
                                                    item.isGracePeriod
                                                        ? { color: color.success }
                                                        : item.isOverstay || item.condition
                                                          ? { color: color.danger }
                                                          : undefined
                                                }
                                            >
                                                {item.value}
                                            </Typography>
                                        </motion.div>
                                    )}
                                </Stack>
                            </Stack>
                        );

                        return (
                            <>
                                {props.plateInput !== undefined && (
                                    <>
                                        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: '1 1 auto' }}>
                                            <TextField
                                                value={props.plateInput}
                                                onChange={(e) => {
                                                    if (props.isPCApprove) return;
                                                    if (e.target.value.includes(' ')) {
                                                        e.target.value = e.target.value.replace(/\s+/g, '');
                                                    }
                                                    props.onPlateInputChange?.(e as React.ChangeEvent<HTMLInputElement>);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (props.isPCApprove) return;
                                                    if (e.key === ' ' || e.code === 'Space') {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    props.onPlateKeyDown?.(e as unknown as React.KeyboardEvent<HTMLInputElement>);
                                                }}
                                                onBlur={props.onPlateBlur}
                                                size="small"
                                                sx={{
                                                    width: 255,
                                                    '& .MuiInputBase-root': {
                                                        backgroundColor: '#FFFF00',
                                                        borderRadius: '4px',
                                                        height: '100%',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontWeight: 600,
                                                        padding: '8px',
                                                        fontSize: '0.925rem',
                                                    },
                                                    alignSelf: 'stretch',
                                                }}
                                                inputProps={{ maxLength: 20 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mr: 0.5, whiteSpace: 'nowrap', fontSize: '0.85rem' }}
                                                        >
                                                            Plate:
                                                        </Typography>
                                                    ),
                                                    endAdornment: !props.isPCApprove ? (
                                                        <EditIcon
                                                            style={{
                                                                width: 30,
                                                                height: 30,
                                                                stroke: color.textPrimary,
                                                            }}
                                                        />
                                                    ) : undefined,
                                                }}
                                            />
                                            {!props.isPCApprove &&
                                                !!props.plateInput &&
                                                props.plateInput !== selectedPc?.Plate && (
                                                    <Fade
                                                    in={!!props.plateInput && props.plateInput !== selectedPc?.Plate}
                                                        timeout={500}
                                                    >
                                                        <LoadingButton
                                                            size="small"
                                                            variant="contained"
                                                            onClick={props.onSavePlate}
                                                            disabled={
                                                                !props.plateInput || props.plateInput === selectedPc?.Plate
                                                            }
                                                            loading={props.updatingPlate}
                                                            sx={{
                                                                minWidth: 60,
                                                                textTransform: 'none',
                                                                fontSize: 13,
                                                                py: 0.25,
                                                            }}
                                                        >
                                                            Save
                                                        </LoadingButton>
                                                    </Fade>
                                                )}
                                        </Stack>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            sx={{ borderColor: color.grey300, mx: 1 }}
                                        />
                                    </>
                                )}

                                {allItems.map(renderItem)}
                            </>
                        );
                    })()}
                </Stack>
            </Stack>
            <Box
                sx={{
                    width: '100%',
                    p: '12px 16px',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                }}
            >
                {isNoOverstayAlert && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 1.5,
                            borderRadius: '4px',
                            fontSize: 13,
                            '& .MuiAlert-message': { fontWeight: 500 },
                        }}
                    >
                        Vehicle duration is within the grace period —{' '}
                        <strong>No Overstay</strong> detected.
                    </Alert>
                )}

                {isCheckDurationExceeding5Minutes && isCheckedDuplicateImageExitAndValidationPhoto && (
                    <OrangeStack mb={2} direction={'column'} gap={1}>
                        <Stack direction={'row'} spacing={1}>
                            <Typography variant="body1">
                                Validation Time = Exit Time (only two evident photos)
                            </Typography>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ display: 'flex' }}
                                color={color.textPrimary}
                            />
                            <Typography variant="body1" fontWeight={550}>{`Total duration > 10 minutes.`}</Typography>
                            <Typography variant="body1">
                                We have determined that the vehicle is possibly in motion. Please review this case.
                            </Typography>
                        </Stack>
                    </OrangeStack>
                )}

                <Box sx={{ width: '100%', margin: '0 auto' }}>
                    <Box
                        ref={sliderRef}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(4, 1fr)`,
                            gap: 1.5,
                            px: 1,
                        }}
                    >
                        {photos.map((item, index) => {
                            const isEmptySlot = !item.photo?.CameraPhoto?.BlobName;
                            const itemVehicleBBoxes = (() => {
                                if (!item.photo) return [];
                                const warpedBox = findVehicleWarpedBox(item.photo);
                                if (!warpedBox) return [];
                                const ca = convertWarpedBoxToCropArea(warpedBox);
                                if (!ca) return [];
                                return [
                                    {
                                        x: ca.x / scaleSize,
                                        y: ca.y / scaleSize,
                                        width: ca.width / scaleSize,
                                        height: ca.height / scaleSize,
                                        plate: item.photo.Plate || item.photo.RawPlate || '',
                                        color:
                                            item.photoType != null
                                                ? photoTypeColorMap[item.photoType]?.main
                                                : undefined,
                                    },
                                ];
                            })();
                            return (
                                <Box key={item.title} sx={{ position: 'relative' }}>
                                    {isEmptySlot ? (
                                        <Stack
                                            sx={{
                                                border: `2px dashed ${color.grey400}`,
                                                borderRadius: 1,
                                                height: '100%',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'default',
                                                userSelect: 'none',
                                                px: 2,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography fontWeight={650} sx={{ whiteSpace: 'nowrap' }}>
                                                Full license plate photo
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Not available.
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <EvidencePhotoItem
                                            title={item.title}
                                            blob={item.blob}
                                            rootBlob={item.rootBlob}
                                            timeZone={item.timeZone}
                                            photoType={item.photoType!}
                                            plateRootImage={item.plateRootImage}
                                            VerificationAudit={props?.VerificationAudit}
                                            date={item.date!}
                                            plate={item.plate}
                                            handleClick={() => {
                                                const vehicleId = item.photo?.Id;
                                                const opened =
                                                    vehicleId && timelineRef.current
                                                        ? timelineRef.current.openPreviewAtPhotoId(vehicleId)
                                                        : false;
                                                if (!opened) {
                                                    setFallbackPreviewIndex(index);
                                                }
                                            }}
                                            handleEditImg={() => {
                                                props.handleSelectEvidencePhotoItem(item as any);
                                                popUpEditImage.setTrue();
                                            }}
                                            handlePlateEdit={() => {
                                                props.handleSelectEvidencePhotoItem(item as any);
                                                popUpPlateEditing.setTrue();
                                            }}
                                            handlePlateScaleEdit={() => {
                                                props.handleSelectEvidencePhotoItem(item as any);
                                            }}
                                            handleReplacePhoto={() => {
                                                props.handleSelectEvidencePhotoItem(item as any);
                                                popUpReplacePhoto.setTrue();
                                            }}
                                            photo={item.photo!}
                                            readonly={props.readonly}
                                            photos={photos}
                                            selectedItem={props.selectedEvidencePhotoItem}
                                            prevGeoZones={props.prevGeoZones}
                                            vehicleBoundingBoxes={itemVehicleBBoxes}
                                            geoZoneDisplayWidth={imageDimensions.width}
                                            onJumpToTimeline={
                                                item.photo?.CameraPhotoId
                                                    ? () => {
                                                          timelineRef.current?.scrollToPhotoId(
                                                              item.photo!.CameraPhotoId!
                                                          );
                                                      }
                                                    : undefined
                                            }
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                {props.selected && (
                    <LivePhotosTimeline
                        ref={timelineRef}
                        pc={pc}
                        photos={photos}
                        prevGeoZones={props.prevGeoZones}
                        geoZoneDisplayWidth={imageDimensions.width}
                    />
                )}
            </Box>

            <PopUpWarning
                {...popUpConfirmCancelPcHasProperty}
                message="Are you sure you want to cancel this PC?"
                onConfirm={handleCancelPCHasProperty}
            />

            <ConfirmApproveDialog
                open={confirmApproveOpen}
                onClose={() => setConfirmApproveOpen(false)}
                summary={verificationSummary}
                onApprove={props.onApprove}
            />

            <LivePhotoPreviewDialog
                open={fallbackPreviewIndex >= 0}
                onClose={() => setFallbackPreviewIndex(-1)}
                items={fallbackItems}
                initialIndex={fallbackPreviewIndex >= 0 ? fallbackPreviewIndex : 0}
                hasMore={false}
                onLoadMore={() => {}}
                timezone={pc.Timezone || 'UTC'}
                prevGeoZones={props.prevGeoZones}
                geoZoneDisplayWidth={imageDimensions.width}
            />

            {props.selectedEvidencePhotoItem?.blob && (
                <>
                    <PopUpEditImage {...popUpEditImage} selectedItem={props.selectedEvidencePhotoItem} />

                    <PopUpPlateEditing
                        {...popUpPlateEditing}
                        selectedItem={props.selectedEvidencePhotoItem}
                        exitPhoto={exitPhoto}
                        validationPhoto={validationPhoto}
                        entryPhoto={entryPhoto}
                        unobstructedPlatePhoto={unobstructedPlatePhoto}
                    />
                </>
            )}

            <PopUpReplacePhoto
                {...popUpReplacePhoto}
                onPreviewChange={(v) => setFallbackPreviewIndex(v ? 0 : -1)}
                onSetSelectedPhotos={handleSetSelectedItem}
            />
        </Stack>
    );
};

export default EvidencePhotoGroup;

type DuplicatesChipProps = {
    pcnDuplicatesWarning: PCNDuplicate[];
    filteredDuplicates: PCNDuplicate[];
    currentStep?: number;
    onOpenDetail?: () => void;
    handleOpenPopUpGrPcDuplicate?: () => void;
};

const DuplicatesChip: React.FC<DuplicatesChipProps> = ({
    pcnDuplicatesWarning,
    filteredDuplicates,
    currentStep,
    onOpenDetail,
    handleOpenPopUpGrPcDuplicate,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const groupingItems = currentStep === 1 ? filteredDuplicates : [];
    const groupableItems = groupingItems.filter((dup) => dup.status !== ContraventionStatus.SystemCancelled);
    const allDuplicates = [...pcnDuplicatesWarning, ...groupingItems].filter(
        (dup, idx, self) => self.findIndex((d) => d.id === dup.id) === idx
    );
    const count = allDuplicates.length;

    return (
        <>
            {/* Pulsing chip */}
            <Box
                onClick={handleClick}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.35,
                    borderRadius: '20px',
                    bgcolor: `${color.danger}18`,
                    border: `1.5px solid ${color.danger}55`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: `${color.danger}28`,
                        border: `1.5px solid ${color.danger}99`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px ${color.danger}33`,
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                }}
            >
                {/* Pulsing dot */}
                <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            bgcolor: color.danger,
                            '@keyframes ping': {
                                '0%': { transform: 'scale(1)', opacity: 0.8 },
                                '75%, 100%': { transform: 'scale(2.2)', opacity: 0 },
                            },
                            animation: 'ping 1.4s cubic-bezier(0, 0, 0.2, 1) infinite',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            bgcolor: color.danger,
                        }}
                    />
                </Box>

                <Typography
                    fontSize={13}
                    fontWeight={600}
                    sx={{ color: color.darkDanger, lineHeight: 1, whiteSpace: 'nowrap' }}
                >
                    Has duplicates{count > 0 ? ` (${count})` : ''}
                </Typography>
            </Box>

            {/* Quick-view Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            borderRadius: '4px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                            border: `1px solid ${color.danger}28`,
                            minWidth: 500,
                            maxWidth: 640,
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        px: 2,
                        py: 1.25,
                        background: `linear-gradient(135deg, ${color.pink50} 0%, ${color.error50} 100%)`,
                        borderBottom: `1px solid ${color.danger}22`,
                    }}
                >
                    {/* Pulsing dot in header */}
                    <Box sx={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                bgcolor: color.danger,
                                '@keyframes pingHeader': {
                                    '0%': { transform: 'scale(1)', opacity: 0.8 },
                                    '75%, 100%': { transform: 'scale(2.5)', opacity: 0 },
                                },
                                animation: 'pingHeader 1.4s cubic-bezier(0, 0, 0.2, 1) infinite',
                            }}
                        />
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: color.danger }} />
                    </Box>
                    <Typography fontWeight={600} fontSize={14} sx={{ color: color.darkDanger, flex: 1 }}>
                        Duplicate
                    </Typography>
                </Stack>

                {/* Body */}
                <Stack
                    spacing={1}
                    sx={{
                        px: 2,
                        py: 1.5,
                        maxHeight: 280,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: `${color.danger}44`,
                            borderRadius: 1,
                        },
                    }}
                >
                    {pcnDuplicatesWarning.length === 0 && (currentStep !== 1 || filteredDuplicates.length === 0) && (
                        <Typography fontSize={13} color="text.secondary" sx={{ py: 1 }}>
                            No duplicate details available.
                        </Typography>
                    )}
                    {pcnDuplicatesWarning.length > 0 && (
                        <Stack spacing={1}>
                            <Typography fontSize={13}>
                                A duplicate contravention for the same location, zone, VRN and contravention reason has been
                                create within a 24-hour period. Please review these contraventions
                            </Typography>
                            {pcnDuplicatesWarning.map((dup, i) => (
                                <Box
                                    key={`warning-${dup.id}-${i}`}
                                    sx={{ borderRadius: '4px', border: `1px solid ${color.danger}22`, overflow: 'hidden' }}
                                >
                                    <DuplicatePcnCard
                                        item={dup}
                                        detailUrl={
                                            dup.source === 'Stella'
                                                ? `${appConfig.apps.stella}/ContraventionManagement/Contravention/Details/${dup.id}`
                                                : `/verification/contravention/${dup.id}`
                                        }
                                        bgColor={color.pink50}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    )}
                    {currentStep === 1 && filteredDuplicates.length > 0 && (
                        <Stack spacing={1}>
                            {pcnDuplicatesWarning.length > 0 && (
                                <Divider sx={{ borderColor: `${color.danger}22` }} />
                            )}
                            {groupableItems.length > 0 && (
                                <Typography fontSize={13} fontWeight={600} sx={{ color: color.danger }}>
                                    Group the following duplicate PCNs into a single group?
                                </Typography>
                            )}
                            {filteredDuplicates.map((dup, i) => (
                                <Box
                                    key={`grouping-${dup.id}-${i}`}
                                    sx={{ borderRadius: '4px', border: `1px solid ${color.danger}22`, overflow: 'hidden' }}
                                >
                                    <DuplicatePcnCard
                                        item={dup}
                                        detailUrl={
                                            dup.source === 'Stella'
                                                ? `${appConfig.apps.stella}/ContraventionManagement/Contravention/Details/${dup.id}`
                                                : `/verification/contravention/${dup.id}`
                                        }
                                        bgColor={color.pink50}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Stack>

                {/* Footer */}
                {onOpenDetail && (
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        sx={{
                            px: 2,
                            py: 1,
                            borderTop: `1px solid ${color.danger}18`,
                            background: color.pink50,
                        }}
                        spacing={2}
                    >
                        <Button
                            size="small"
                            onClick={() => {
                                handleClose();
                                onOpenDetail();
                            }}
                            sx={{
                                textTransform: 'none',
                                fontSize: 12,
                                fontWeight: 600,
                                color: color.darkDanger,
                                bgcolor: `${color.danger}14`,
                                '&:hover': { bgcolor: `${color.danger}20` },
                            }}
                        >
                            View full details
                        </Button>
                        {currentStep === 1 && groupableItems.length > 0 && (
                            <Button
                                size="small"
                                onClick={() => {
                                    handleOpenPopUpGrPcDuplicate?.();
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    bgcolor: `${color.success}14`,
                                    '&:hover': { bgcolor: `${color.success}20` },
                                }}
                            >
                                Confirm grouping
                            </Button>
                        )}
                    </Stack>
                )}
            </Popover>
        </>
    );
};

type PermitChipProps = {
    permitData: PermitData;
    onOpenDetail?: () => void;
    onCancelPermit?: () => void;
};

const PermitChip: React.FC<PermitChipProps> = ({ permitData, onOpenDetail, onCancelPermit }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const isGlobal = permitData?.permitInfo?.isGlobalPermit;
    const label = isGlobal ? 'Has global permit' : 'Has permit';

    return (
        <>
            {/* Pulsing chip */}
            <Box
                onClick={handleClick}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.35,
                    borderRadius: '20px',
                    bgcolor: `${color.gold600}18`,
                    border: `1.5px solid ${color.gold600}55`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: `${color.gold600}28`,
                        border: `1.5px solid ${color.gold600}99`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 2px 8px ${color.gold600}33`,
                    },
                    '&:active': { transform: 'translateY(0)' },
                }}
            >
                {/* Pulsing dot */}
                <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            bgcolor: color.gold600,
                            '@keyframes pingPermit': {
                                '0%': { transform: 'scale(1)', opacity: 0.9 },
                                '75%, 100%': { transform: 'scale(2.2)', opacity: 0 },
                            },
                            animation: 'pingPermit 1.4s cubic-bezier(0, 0, 0.2, 1) infinite',
                        }}
                    />
                    <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: color.gold600 }} />
                </Box>
                <Typography
                    fontSize={13}
                    fontWeight={600}
                    sx={{ color: color.gold600, lineHeight: 1, whiteSpace: 'nowrap' }}
                >
                    {label}
                </Typography>
            </Box>

            {/* Quick-view Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            borderRadius: '4px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                            border: `1px solid ${color.gold600}30`,
                            minWidth: 360,
                            maxWidth: 500,
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        px: 2,
                        py: 1.25,
                        background: `linear-gradient(135deg, ${color.gold100} 0%, ${color.gold200} 100%)`,
                        borderBottom: `1px solid ${color.gold600}25`,
                    }}
                >
                    <Box sx={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                bgcolor: color.gold600,
                                '@keyframes pingPermitHeader': {
                                    '0%': { transform: 'scale(1)', opacity: 0.9 },
                                    '75%, 100%': { transform: 'scale(2.5)', opacity: 0 },
                                },
                                animation: 'pingPermitHeader 1.4s cubic-bezier(0, 0, 0.2, 1) infinite',
                            }}
                        />
                        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: color.gold600 }} />
                    </Box>
                    <Typography fontWeight={600} fontSize={14} sx={{ color: color.gold800, flex: 1 }}>
                        {isGlobal ? 'Global Permit' : 'Permit Details'}
                    </Typography>
                    {isGlobal && (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                bgcolor: color.gold600,
                                borderRadius: '12px',
                                color: color.white,
                                fontSize: 11,
                                fontWeight: 700,
                                lineHeight: 1.4,
                            }}
                        >
                            Global
                        </Box>
                    )}
                </Stack>

                {/* Body */}
                <Stack
                    spacing={1}
                    sx={{
                        px: 2,
                        py: 1.5,
                        background: color.gold100,
                    }}
                >
                    <Typography fontSize={13} sx={{ color: color.gold800 }}>
                        Permit exists for this VRN within this location at the time of issue.
                    </Typography>
                    <Stack direction="column" spacing={1.5} flexWrap="wrap">
                        {permitData?.permitInfo?.bayNumber != null && (
                            <Typography fontSize={13} fontWeight={600} sx={{ color: color.gold800 }}>
                                Bay: {permitData.permitInfo.bayNumber}
                            </Typography>
                        )}
                        {permitData?.permitInfo?.source && (
                            <Typography fontSize={13} fontWeight={600} sx={{ color: color.gold800 }}>
                                Source: {permitData.permitInfo.source}
                            </Typography>
                        )}
                        {permitData?.permitInfo?.tenant && (
                            <Typography fontSize={13} fontWeight={600} sx={{ color: color.gold800 }}>
                                Tenant: {permitData.permitInfo.tenant}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                {/* Footer */}
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1.5}
                    sx={{
                        px: 2,
                        py: 1,
                        borderTop: `1px solid ${color.gold600}20`,
                        background: color.gold200,
                    }}
                >
                    {onOpenDetail && (
                        <Button
                            size="small"
                            onClick={() => {
                                handleClose();
                                onOpenDetail();
                            }}
                            sx={{
                                textTransform: 'none',
                                fontSize: 12,
                                fontWeight: 600,
                                color: color.gold800,
                                bgcolor: `${color.gold600}18`,
                                '&:hover': { bgcolor: `${color.gold600}28` },
                            }}
                        >
                            View full details
                        </Button>
                    )}
                    {onCancelPermit && (
                        <Button
                            size="small"
                            onClick={() => {
                                handleClose();
                                onCancelPermit();
                            }}
                            sx={{
                                textTransform: 'none',
                                fontSize: 12,
                                fontWeight: 600,
                                color: color.darkDanger,
                                bgcolor: `${color.danger}14`,
                                '&:hover': { bgcolor: `${color.danger}20` },
                            }}
                        >
                            Cancel this PC
                        </Button>
                    )}
                </Stack>
            </Popover>
        </>
    );
};

type PulsingChipProps = { label: string; mainColor: string; textColor: string; animationKey: string };

const PulsingChip: React.FC<PulsingChipProps> = ({ label, mainColor, textColor, animationKey }) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.35,
            borderRadius: '20px',
            bgcolor: `${mainColor}18`,
            border: `1.5px solid ${mainColor}55`,
            userSelect: 'none',
        }}
    >
        <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    bgcolor: mainColor,
                    [`@keyframes ${animationKey}`]: {
                        '0%': { transform: 'scale(1)', opacity: 0.8 },
                        '75%, 100%': { transform: 'scale(2.2)', opacity: 0 },
                    },
                    animation: `${animationKey} 1.4s cubic-bezier(0, 0, 0.2, 1) infinite`,
                }}
            />
            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: mainColor }} />
        </Box>
        <Typography fontSize={13} fontWeight={600} sx={{ color: textColor, lineHeight: 1, whiteSpace: 'nowrap' }}>
            {label}
        </Typography>
    </Box>
);

export const Controls = ({
    showPrevGeoZones,
    showCropArea,
    showVehicleBBoxes,
    onSetShowPrevGeoZones,
    onSetShowCropArea,
    onSetShowVehicleBBoxes,
    togglePrevGeoZones,
    toggleCropArea,
    toggleVehicleBBoxes,
    showZoomIn,
    showZoomOut,
}: {
    showPrevGeoZones?: boolean;
    showCropArea?: boolean;
    showVehicleBBoxes?: boolean;
    onSetShowPrevGeoZones?: (geoZones: boolean) => void;
    onSetShowCropArea?: (cropArea: boolean) => void;
    onSetShowVehicleBBoxes?: (show: boolean) => void;
    togglePrevGeoZones?: () => void;
    toggleCropArea?: () => void;
    toggleVehicleBBoxes?: () => void;
    showZoomIn?: boolean;
    showZoomOut?: boolean;
}) => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    const zoomIcons = [
        showZoomIn ? { icon: <ZoomImageIcon isChecked={false} />, label: 'Zoom in', id: 1 } : undefined,
        showZoomOut ? { icon: <MinimizeImageIcon isChecked={false} />, label: 'Zoom out', id: 2 } : undefined,
    ].filter((item) => item !== undefined) as { icon: JSX.Element; label: string; id: number }[];

    const resetIcon = { icon: <RestartImageIcon isChecked={false} />, label: 'Reset', id: 3 };

    const additionalIcons = [
        showPrevGeoZones !== undefined
            ? {
                  icon: (
                      <Stack border={'3px solid #FFFFFF'} borderRadius={'50%'} p={'4px'}>
                          <CoordinatesIcon />
                      </Stack>
                  ),
                  label: showPrevGeoZones ? 'Hide geo-zones coordinates' : 'Show geo-zones coordinates',
                  id: 4,
              }
            : undefined,
        showCropArea !== undefined
            ? {
                  icon: (
                      <Stack border={'3px solid #FFFFFF'} borderRadius={'50%'} p={'4px'}>
                          <CropPlate />
                      </Stack>
                  ),
                  label: showCropArea ? 'Hide crop plate' : 'Show crop plate',
                  id: 5,
              }
            : undefined,
        showVehicleBBoxes !== undefined
            ? {
                  icon: (
                      <Stack border={'3px solid #FFFFFF'} borderRadius={'50%'} p={'4px'}>
                          <BoundingBoxVehicle />{' '}
                      </Stack>
                  ),
                  label: showVehicleBBoxes ? 'Hide vehicle bounding boxes' : 'Show vehicle bounding boxes',
                  id: 6,
              }
            : undefined,
    ].filter((item) => item !== undefined) as { icon: JSX.Element; label: string; id: number }[];

    const iconsAction = [...zoomIcons, resetIcon, ...additionalIcons];

    return (
        <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            sx={{
                position: 'absolute',
                top: '2%',
                left: '50%',
                zIndex: 2,
                transform: 'translateX(-50%)',
                width: 'fit-content',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                borderRadius: '8px',
                padding: '4px 8px',
                backdropFilter: 'blur(4px)',
            }}
        >
            {iconsAction.map((icon, index) => (
                <Tooltip title={icon.label} arrow placement="bottom" key={index}>
                    <IconButton
                        size="small"
                        sx={{
                            color: '#fff',
                            padding: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                        onClick={() => {
                            const buttonActions: Record<number, any> = {
                                1: zoomIn,
                                2: zoomOut,
                                3: () => {
                                    resetTransform();
                                    onSetShowPrevGeoZones?.(true);
                                    onSetShowCropArea?.(true);
                                    onSetShowVehicleBBoxes?.(true);
                                },
                                4: togglePrevGeoZones,
                                5: toggleCropArea,
                                6: toggleVehicleBBoxes,
                            };

                            buttonActions[icon.id]?.();
                        }}
                    >
                        <Stack sx={{ transform: 'scale(0.8)' }}>{icon.icon}</Stack>
                    </IconButton>
                </Tooltip>
            ))}
        </Stack>
    );
};

export const TransformWrapperComponent: React.FC<TransformWrapperComponentProps> = ({
    handleTransform,
    loading,
    displayWidth,
    displayHeight,
    scale,
    scaleZoomChangeCursor,
    image,
    stageRef,
    customTransformComponent,
    prevGeoZones,
    cropArea,
    cameraPhotoVehicles,
    vehicleBoundingBoxes,
    adjustmentEnabled,
    initialBrightness,
    initialContrast,
    onSaveAdjustment,
    useCustomDisplayWidth,
    showZoomIn = true,
    showZoomOut = true,
    transformWrapperRef,
    showOverlays = true,
    onInteraction,
}) => {
    const [showPrevGeoZones, setShowPrevGeoZones] = useState<boolean>(true);
    const [showCropArea, setShowCropArea] = useState<boolean>(true);
    const [showVehicleBBoxes, setShowVehicleBBoxes] = useState<boolean>(true);
    const [brightness, setBrightness] = useState<number>(initialBrightness ?? 0);
    const [contrast, setContrast] = useState<number>(initialContrast ?? 0);
    const imageNodeRef = useRef<any>(null);
    const imageLayerRef = useRef<any>(null);
    const overlayLayerRef = useRef<any>(null);

    useEffect(() => {
        const handleOverlayKeyDown = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            const key = e.key.toLowerCase();
            if (key === 'q') {
                if (prevGeoZones?.length) setShowPrevGeoZones((prev) => !prev);
                if (cropArea || cameraPhotoVehicles?.length) setShowCropArea((prev) => !prev);
                if (vehicleBoundingBoxes?.length) setShowVehicleBBoxes((prev) => !prev);
            }
        };
        window.addEventListener('keydown', handleOverlayKeyDown);
        return () => window.removeEventListener('keydown', handleOverlayKeyDown);
    }, [prevGeoZones, cropArea, cameraPhotoVehicles, vehicleBoundingBoxes]);

    useEffect(() => {
        const node = imageNodeRef.current;
        if (!node) return;
        if (adjustmentEnabled && image) {
            try {
                node.cache();
                imageLayerRef.current?.batchDraw?.();
            } catch {}
        } else {
            try {
                node.clearCache();
                imageLayerRef.current?.batchDraw?.();
            } catch {}
        }
    }, [image, adjustmentEnabled]);

    useEffect(() => {
        if (!adjustmentEnabled) return;
        const node = imageNodeRef.current;
        if (node) {
            try {
                imageLayerRef.current?.batchDraw?.();
            } catch {}
        }
    }, [brightness, contrast]);

    const heldKeysRef = useRef<Set<string>>(new Set());

    const handleSave = () => {
        imageNodeRef.current?.cache?.();
        imageLayerRef.current?.batchDraw?.();

        const layerCanvas = imageLayerRef.current?.getCanvas()?._canvas;
        const originalWidth = image?.width;
        const originalHeight = image?.height;

        if (layerCanvas && originalWidth && originalHeight) {
            const exportCanvas = document.createElement('canvas');
            exportCanvas.width = originalWidth;
            exportCanvas.height = originalHeight;
            const ctx = exportCanvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(
                    layerCanvas,
                    0,
                    0,
                    layerCanvas.width,
                    layerCanvas.height,
                    0,
                    0,
                    originalWidth,
                    originalHeight
                );
                const dataUrl = exportCanvas.toDataURL('image/jpeg');
                onSaveAdjustment?.(dataUrl);
            }
        }
    };

    useEffect(() => {
        if (!adjustmentEnabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            const key = e.key.toLowerCase();
            heldKeysRef.current.add(key);

            if (key === 'arrowup' || key === 'arrowdown') {
                const direction = key === 'arrowup' ? 1 : -1;

                if (heldKeysRef.current.has('b')) {
                    e.preventDefault();
                    onInteraction?.();
                    setBrightness((prev) => Math.min(1, Math.max(-1, +(prev + direction * 0.01).toFixed(2))));
                } else if (heldKeysRef.current.has('c')) {
                    e.preventDefault();
                    onInteraction?.();
                    setContrast((prev) => Math.min(100, Math.max(-100, prev + direction * 1)));
                }
            }

            if (key === 'enter') {
                e.preventDefault();
                handleSave();
            }

            if (key === 'r') {
                e.preventDefault();
                onInteraction?.();
                setBrightness(0);
                setContrast(0);
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            heldKeysRef.current.delete(e.key.toLowerCase());
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            heldKeysRef.current.clear();
        };
    }, [adjustmentEnabled, image]);

    return (
        <TransformWrapper
            ref={transformWrapperRef}
            alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
            doubleClick={{ disabled: true }}
            wheel={{ disabled: false }}
            centerZoomedOut={true}
            onTransformed={(e) => handleTransform?.(e)}
        >
            {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                <>
                    <Box
                        sx={{
                            opacity: showOverlays ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            pointerEvents: showOverlays ? 'auto' : 'none',
                        }}
                    >
                        <Controls
                            showPrevGeoZones={prevGeoZones?.length ? showPrevGeoZones : undefined}
                            showCropArea={cropArea || cameraPhotoVehicles?.length ? showCropArea : undefined}
                            showVehicleBBoxes={vehicleBoundingBoxes?.length ? showVehicleBBoxes : undefined}
                            onSetShowPrevGeoZones={setShowPrevGeoZones}
                            onSetShowCropArea={setShowCropArea}
                            onSetShowVehicleBBoxes={setShowVehicleBBoxes}
                            togglePrevGeoZones={() => setShowPrevGeoZones((prev) => !prev)}
                            toggleCropArea={() => setShowCropArea((prev) => !prev)}
                            toggleVehicleBBoxes={() => setShowVehicleBBoxes((prev) => !prev)}
                            showZoomIn={showZoomIn}
                            showZoomOut={showZoomOut}
                        />
                    </Box>
                    <TransformComponent>
                        {!customTransformComponent ? (
                            <Stack
                                sx={{
                                    position: 'relative',
                                }}
                            >
                                {loading ? (
                                    <Stack
                                        direction="row"
                                        justifyContent="center"
                                        alignItems="center"
                                        minHeight={displayHeight}
                                        minWidth={displayWidth}
                                        color={'#E7E9ED'}
                                    >
                                        <CircularProgress sx={{ margin: 'auto' }} />
                                    </Stack>
                                ) : (
                                    <Stage
                                        ref={stageRef}
                                        width={displayWidth}
                                        height={displayHeight}
                                        style={{
                                            cursor: scaleZoomChangeCursor !== 1 ? 'grab' : 'default',
                                        }}
                                    >
                                        <Layer ref={imageLayerRef}>
                                            <KonvaImage
                                                ref={imageNodeRef}
                                                image={image}
                                                scaleX={scale}
                                                scaleY={scale}
                                                {...(adjustmentEnabled
                                                    ? {
                                                          filters: [Konva.Filters.Brighten, Konva.Filters.Contrast],
                                                          brightness: brightness,
                                                          contrast: contrast,
                                                      }
                                                    : {})}
                                            />
                                        </Layer>
                                        <Layer ref={overlayLayerRef}>
                                            {showPrevGeoZones &&
                                                !!prevGeoZones?.length &&
                                                prevGeoZones
                                                    ?.filter((frame) => frame[0].geoZoneId)
                                                    ?.map((frame, frameIndex) => {
                                                        if (!frame || !frame.length) {
                                                            return null;
                                                        }

                                                        const center = getCenterFrame(frame);
                                                        return (
                                                            <React.Fragment key={`frame-${frameIndex}`}>
                                                                <Line
                                                                    points={frame.flatMap((point) => [
                                                                        point.x,
                                                                        point.y,
                                                                    ])}
                                                                    stroke={color.accentPink}
                                                                    fill="rgba(0, 0, 0, 0.12)"
                                                                    dash={[10, 5]}
                                                                    strokeWidth={2}
                                                                    closed={true}
                                                                />
                                                                <Circle
                                                                    x={center.x}
                                                                    y={center.y}
                                                                    radius={15}
                                                                    fill={color.white}
                                                                />
                                                                <Text
                                                                    x={center.x}
                                                                    y={center.y}
                                                                    text={String(`${frameIndex + 1}`)}
                                                                    fontSize={14}
                                                                    fill={color.textPrimary}
                                                                    align={'center'}
                                                                    verticalAlign={'middle'}
                                                                    offsetX={4}
                                                                    offsetY={6}
                                                                />
                                                            </React.Fragment>
                                                        );
                                                    })}

                                            {showCropArea && cropArea && (
                                                <Rect
                                                    x={cropArea.x}
                                                    y={cropArea.y}
                                                    width={cropArea.width}
                                                    height={cropArea.height}
                                                    stroke="red"
                                                    strokeWidth={3}
                                                />
                                            )}
                                            {showCropArea &&
                                                !!cameraPhotoVehicles?.length &&
                                                cameraPhotoVehicles?.map((vehicle, index) => {
                                                    const rootImageWidth = image?.width || 1920;
                                                    const scaleSize = useCustomDisplayWidth
                                                        ? rootImageWidth / (displayWidth || 1280)
                                                        : rootImageWidth / 1280;
                                                    const cropArea = convertToCropArea({
                                                        X1: vehicle.X1 / scaleSize,
                                                        X2: vehicle.X2 / scaleSize,
                                                        X3: vehicle.X3 / scaleSize,
                                                        X4: vehicle.X4 / scaleSize,
                                                        Y1: vehicle.Y1 / scaleSize,
                                                        Y2: vehicle.Y2 / scaleSize,
                                                        Y3: vehicle.Y3 / scaleSize,
                                                        Y4: vehicle.Y4 / scaleSize,
                                                    });

                                                    return (
                                                        <Rect
                                                            key={`crop-area-${index}`}
                                                            x={cropArea.x}
                                                            y={cropArea.y}
                                                            width={cropArea.width}
                                                            height={cropArea.height}
                                                            stroke="red"
                                                            strokeWidth={3}
                                                        />
                                                    );
                                                })}

                                            {showVehicleBBoxes &&
                                                !!vehicleBoundingBoxes?.length &&
                                                vehicleBoundingBoxes.map((bbox, index) => (
                                                    <React.Fragment key={`vehicle-bbox-${index}`}>
                                                        <Rect
                                                            x={bbox.x}
                                                            y={bbox.y}
                                                            width={bbox.width}
                                                            height={bbox.height}
                                                            stroke={bbox.color ?? color.mint}
                                                            strokeWidth={2}
                                                            shadowColor={bbox.color ?? color.mint}
                                                            shadowBlur={8}
                                                        />
                                                        {bbox.plate && bbox.plate !== 'NaN' &&
                                                            (() => {
                                                                const textFontSize = 18;
                                                                const padX = 5;
                                                                const padY = 6;
                                                                const estimatedW =
                                                                    bbox.plate.length * textFontSize * 0.62 + padX * 2;
                                                                const estimatedH = textFontSize + padY * 2;
                                                                const TEXT_ABOVE_THRESHOLD = estimatedH + padY + 30;
                                                                const isAbove = bbox.y > TEXT_ABOVE_THRESHOLD;
                                                                const textY = isAbove
                                                                    ? bbox.y - 25
                                                                    : bbox.y + bbox.height + 5;
                                                                const rectX = bbox.x - padX;
                                                                const rectY = isAbove
                                                                    ? bbox.y - 25 - padY
                                                                    : bbox.y + bbox.height;
                                                                return (
                                                                    <React.Fragment>
                                                                        <Rect
                                                                            x={rectX}
                                                                            y={rectY}
                                                                            width={estimatedW}
                                                                            height={estimatedH}
                                                                            fill="rgba(0,0,0,0.55)"
                                                                            cornerRadius={3}
                                                                            shadowColor="black"
                                                                            shadowBlur={8}
                                                                            shadowOpacity={0.6}
                                                                        />
                                                                        <Text
                                                                            x={bbox.x}
                                                                            y={textY}
                                                                            text={bbox.plate}
                                                                            fontSize={textFontSize}
                                                                            fill={bbox.color ?? color.mint}
                                                                            strokeWidth={0.5}
                                                                        />
                                                                    </React.Fragment>
                                                                );
                                                            })()}
                                                    </React.Fragment>
                                                ))}
                                        </Layer>
                                    </Stage>
                                )}
                            </Stack>
                        ) : (
                            <>{customTransformComponent}</>
                        )}
                    </TransformComponent>

                    {adjustmentEnabled && (
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            spacing={1}
                            sx={{
                                position: 'absolute',
                                bottom: '3%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '70%',
                                zIndex: 3,
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                backdropFilter: 'blur(6px)',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                opacity: showOverlays ? 1 : 0,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: showOverlays ? 'auto' : 'none',
                            }}
                        >
                            <Stack direction={'row'} alignItems={'center'} spacing={2} flex={1}>
                                <Typography sx={{ color: color.white }}>Brightness</Typography>
                                <Slider
                                    size="small"
                                    value={brightness}
                                    onChange={(_, v) => setBrightness(v as number)}
                                    onChangeCommitted={() => (document.activeElement as HTMLElement)?.blur?.()}
                                    min={-1}
                                    max={1}
                                    step={0.01}
                                    sx={{ color: color.white }}
                                    valueLabelFormat={(value) => `${value}%`}
                                    valueLabelDisplay="auto"
                                />
                            </Stack>
                            <Stack direction={'row'} alignItems={'center'} spacing={2} flex={1}>
                                <Typography sx={{ color: color.white }}>Contrast</Typography>
                                <Slider
                                    size="small"
                                    value={contrast}
                                    onChange={(_, v) => setContrast(v as number)}
                                    onChangeCommitted={() => (document.activeElement as HTMLElement)?.blur?.()}
                                    min={-100}
                                    max={100}
                                    step={1}
                                    sx={{ color: color.white }}
                                    valueLabelFormat={(value) => `${value}%`}
                                    valueLabelDisplay="auto"
                                />
                            </Stack>
                            <Button
                                variant="cancel"
                                onClick={() => {
                                    setBrightness(0);
                                    setContrast(0);
                                }}
                                size="small"
                                sx={{
                                    py: '6px',
                                }}
                            >
                                Reset
                            </Button>
                            <Button variant="contained" onClick={handleSave} size="small">
                                Save
                            </Button>
                        </Stack>
                    )}
                </>
            )}
        </TransformWrapper>
    );
};
